import { isDeliverableFrame } from "@/lib/frame-classification";
import type { FrameData } from "@/lib/frame-data";

import type { ConversationMessage } from "@/stores/conversation-store";

function metadataString(
  metadata: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}

export function resolveConversationExecutionIds(
  conversationFrameId: string,
  frame: FrameData,
  messages: readonly ConversationMessage[],
): string[] {
  const conversationId = metadataString(
    frame.metadata,
    "conversationId",
    "conversation_id",
  );
  const executionIds = new Set<string>();
  for (const key of ["executionId", "execution_id", "liveExecutionId"]) {
    const executionId = metadataString(frame.metadata, key);
    if (executionId) executionIds.add(executionId);
  }

  for (const message of messages) {
    const belongsToConversation = conversationId
      ? message.conversationId === conversationId
      : message.conversationFrameId === conversationFrameId;
    if (belongsToConversation && message.executionId) {
      executionIds.add(message.executionId);
    }
  }
  return [...executionIds];
}

/** Deliverable frames of these executions, newest first. */
export function resolveArtifactsForExecutionIds(
  executionIds: readonly string[],
  frames: readonly FrameData[],
): FrameData[] {
  if (executionIds.length === 0) return [];
  const executionIdSet = new Set(executionIds);

  return frames
    .filter((frame) => {
      if (!isDeliverableFrame(frame.frameType)) return false;
      const executionId = metadataString(
        frame.metadata,
        "executionId",
        "execution_id",
      );
      return executionId !== null && executionIdSet.has(executionId);
    })
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

export function resolveConversationArtifacts(
  conversationFrameId: string,
  frame: FrameData,
  messages: readonly ConversationMessage[],
  frames: readonly FrameData[],
): FrameData[] {
  const executionIds = resolveConversationExecutionIds(
    conversationFrameId,
    frame,
    messages,
  );
  return resolveArtifactsForExecutionIds(executionIds, frames);
}
